import React, {useCallback, useEffect} from 'react';
import {placeElement} from './overlaps.js';
import {
    setEdgesPadding,
    setEdgesSize,
    setEditorPadding,
    setZObjectSize,
    setSelectedPulledElements,
    setBulkWindowPositions,
    setWindowPosition} from 'actions/index.js';
import {
    getPositions,
    doesElementIntersect,
    getMinLeftAndTop,
    getMaxLeftAndTop} from './utils.js';

export const useWindowPlacer = ({
    element,
    skipPlacement,
    setWindowScale,
    sequencerEngine,
    ref,
    setSize,
    sizeRef,
    containerRef,
    setCoords,
    handleScroll,
    elements
}) => {
        // called once at the beginning
    // places the element based on the x/y stored
    // inside it
    useEffect(() => {
        if (!containerRef || skipPlacement) {
            if (element && element.width !== undefined) {
                if (setWindowScale) {
                    setWindowScale({
                        x: element.width,
                        y: element.height
                    });
                }
            }
            return;
        }


        if (element && element.x !== undefined) {
            setCoords({x: element.x, y: element.y});
            if (element.width !== undefined) {
                setWindowScale({
                    x: element.width,
                    y: element.height
                });
            }
            adjust(element.x, element.y, true);
        }

        if (element && element.x === undefined) {
            if (!sequencerEngine || !sequencerEngine.state.windows.windowPositions[element.id]) {
                placeWindow(element);
            }
        }
        if (element && elements.current) {
            elements.current[element.id] = ref;
        }

        if (element.needsPlacement) {
            // given the fact that we have placed the window in some x,y coordinate
            // there may be some windows that "overlap" this new location
            // we have to then move some of these windows around to fit this new window
            let {x,y,id} = element;
            let newCoords = placeElement(
                x,y,id,ref.current,
                elements.current);
            sequencerEngine.dispatch(setBulkWindowPositions(newCoords));
        }
    }, []);

    const sortByStack = (elements) => {
        if (!elements) {
            return {
                current: {}
            };
        }
        let stack = [... sequencerEngine.state.zobject.zobjectsStack];
        stack.reverse();
        let newElements = {};
        for (let object of stack) {
            newElements[object.id] = elements.current[object.id];
        }
        for (let id in elements.current) {
            newElements[id] = elements.current[id];
        }
        return {
            current: newElements
        };
    };
    // places a window such that it doesn't intersect any existing windows
    const placeWindow = useCallback((element) => {
        if (!containerRef) {
            return;
        }

        // get current position of every element 
        let positions = getPositions(sortByStack(elements));

        // "top" means the y + height btw. essentially "bottom" actually idk why
        // i called it top
        let {tops, lefts, widths, heights} = positions;
        for (let i in tops) {
            let top = tops[i];
            let left = lefts[i];
            let width = widths[i];
            let height = heights[i];

            // x and y position of the
            let x = left + 5;
            let y = top - height ;

            if (x + ref.current.offsetWidth <= containerRef.current.offsetWidth &&
                !doesElementIntersect(
                    x,
                    y,
                    ref.current.offsetWidth,
                    ref.current.offsetHeight,
                    positions)) {
                
                setCoords({x,y});
                adjust(x,y,true);
                return;
            } else {
                x = left - width;
                y = top + 5;
                if (!doesElementIntersect(
                    x,
                    y,
                    ref.current.offsetWidth,
                    ref.current.offsetHeight,
                    positions))
                {

            if (element.id === "afc8c52c-c7e5-46d2-9374-9ea3f9eb5912") {
            }
                    setCoords({x,y});
                    adjust(x,y,true);
                    return;
                }
            }
        }
    }, []);

    // adjust is called whenever windows change position
    // it essentially calculates the bounding box of the entire field of windows (the total size)
    // it also calculates the "negative padding" of the canvas w/in the editor
    const adjust = useCallback((x,y, skipRest) => {
        if (!sizeRef || !ref.current) {
            return;
        }

        let size = sizeRef.current;
        
        if (size) {
            if (x + ref.current.offsetWidth > size.width ||
                y + ref.current.offsetHeight > size.height) {
                let _size = {
                    width: Math.max(size.width, x + ref.current.offsetWidth),
                    height: Math.max(size.height, y + ref.current.offsetHeight)
                };
                sizeRef.current = _size;
                setSize(_size);
                return;
            }

            if (skipRest) {
                return;
            }


            let {minTop, minLeft} = getMinLeftAndTop(elements);
            let {maxTop, maxLeft} = getMaxLeftAndTop(elements);

            minTop -= 100;
            minLeft -= 100;

            if (sequencerEngine && element.subSequence && !element.subSequence.getRootSubSequence) {
                let {edgesSize, edgesPadding} = sequencerEngine.state.spatial;

                let newSize = {
                    width: maxLeft - minLeft,
                    height: maxTop  - minTop
                };

                if (newSize.width !== edgesSize.width ||
                    newSize.height !== edgesSize.height) {
                    sequencerEngine.dispatch(
                        setEdgesSize(newSize.width + 300, newSize.height + 300));
                }

                if ((edgesPadding.x !== minLeft || edgesPadding.y !== minTop)) {
                    sequencerEngine.dispatch(
                        setEdgesPadding(minLeft,
                                        minTop));
                }
            }

            if (maxLeft < size.width ||
                maxTop < size.height) {
                if (!(maxLeft > size.width || maxTop > size.height)) {
                    return;
                }
                let _size = {
                    width: Math.max(size.width, maxLeft),
                    height: Math.max(size.height, maxTop),
                };
                sizeRef.current = _size;
                setSize(_size);

                if (sequencerEngine) {
                    let {mainScrollRef} = sequencerEngine.state.spatial;
                    if (mainScrollRef) {
                        handleScroll(x,y,sizeRef);
                    }
                }
            }
        }
    }, [setSize, elements]);

    return {adjust, placeWindow};
};
